/**
 * Ported from frontend/shared/src/commonMain/kotlin/sk/myslikrok/shared/Storage/TokenStore.kt.
 * A change to the Kotlin changes this file in the same commit — client code manifest §2a.
 *
 * Two tokens are held at once: the parent's, and — while a child profile is active — a child-scoped one.
 * Switching profile selects between them; it does not replace one with the other, because switching back
 * must not require the parent to sign in again (ADR-0006).
 *
 * **The web has no Keychain and no EncryptedSharedPreferences.** `sessionStorage` is the honest choice
 * here rather than `localStorage`: a token that survives closing the tab is a token left on a shared
 * family computer, and this product is used by children on exactly those. The cost is that a refresh of
 * the tab keeps the session and closing it does not, which is the behaviour a parent would expect anyway.
 *
 * Access to storage is wrapped: a private window with site data blocked throws on read, and the app has to
 * come up signed out rather than not come up at all.
 */
const PARENT_ACCESS = 'myslikrok.parent.access';
const PARENT_REFRESH = 'myslikrok.parent.refresh';
const CHILD_ACCESS = 'myslikrok.child.access';
const CHILD_ID = 'myslikrok.child.id';

const read = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage is unavailable. The session then lives for as long as the page does, which is degraded and
    // still usable — losing it silently is better than refusing to start.
  }
};

const remove = (key: string): void => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // See above.
  }
};

export const TokenStore = {
  readParentAccessToken: (): string | null => read(PARENT_ACCESS),
  readParentRefreshToken: (): string | null => read(PARENT_REFRESH),

  writeParentTokens: (accessToken: string, refreshToken: string): void => {
    write(PARENT_ACCESS, accessToken);
    write(PARENT_REFRESH, refreshToken);
  },

  readChildAccessToken: (): string | null => read(CHILD_ACCESS),
  readActiveChildId: (): string | null => read(CHILD_ID),

  writeChildToken: (childId: string, accessToken: string): void => {
    write(CHILD_ID, childId);
    write(CHILD_ACCESS, accessToken);
  },

  clearChildToken: (): void => {
    remove(CHILD_ACCESS);
    remove(CHILD_ID);
  },

  clearAll: (): void => {
    remove(PARENT_ACCESS);
    remove(PARENT_REFRESH);
    remove(CHILD_ACCESS);
    remove(CHILD_ID);
  },
};

/** The token to send, given which profile is active. */
export const activeAccessToken = (): string | null =>
  TokenStore.readChildAccessToken() ?? TokenStore.readParentAccessToken();

/** True while a child profile is active. */
export const isChildActive = (): boolean => TokenStore.readChildAccessToken() !== null;
